import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  CharacterFeatureSource, 
  CharacterSheetInterface, 
  CreatureInstance, 
  SummonInstance 
} from "@dndModels";

import CommonUtilities from "@utilities/common_utilities";

import StringBox from "@components/input/strings/StringBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import ReusableStringBox from "@dndComponents/ReusableStringBox";
import ButtonBox from "@components/input/buttons/ButtonBox";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";

interface Props {
  name: string;
  value: string;
  cfs?: CharacterSheetInterface | CreatureInstance | SummonInstance | CharacterFeatureSource;
  onChange: (value: string) => void; 
  disabled?: boolean;
}

const common_util = CommonUtilities.getInstance();

export default function StringBoxWithReusables({
  name,
  value,
  cfs,
  onChange,
  disabled = false,
  // log_it = false
}: Props) {
  const [my_value, setValue] = useState(value);
  const [prev_value, setPrevValue] = useState(value);
  const [view_plain, setViewPlain] = useState(false);
  const [change_time, setChangeTime] = useState(0);

  useEffect(() => {
    if (prev_value !== value) {
      setPrevValue(value);
      setValue(value);
    }
  }, [prev_value, value, setPrevValue, setValue]);
  
  const removeTagAttributes = (str: string) => {
    // 1. Unwrap all <a> tags
    // This finds the opening <a> tag and its attributes, the content,
    // and the closing </a> tag, and replaces the whole match with only the content.
    const unwrappedHtml = str.replace(/<a\b[^>]*>(.*?)<\/a>/gi, '$1');

    // 2. Remove attributes from all other tags
    // This finds any opening tag (e.g., <div class="foo"> or <img src="bar">)
    // and removes everything between the tag name and the final '>' character.
    // It keeps self-closing tags (like <br/>) intact by checking for a preceding '/'
    // to avoid removing the content *before* the slash.
    const attributesRemoved = unwrappedHtml.replace(/<(\w+)\s*[^>]*?(\/?)>/g, (match, tagName, selfClose) => {
        // Reconstructs the tag: e.g., <div> or <img/>
        return `<${tagName}${selfClose}>`;
    });

    return attributesRemoved;
  }

  return (
    <div style={{ backgroundColor: "black" }}>
      <Grid container spacing={0} direction="row">
        { disabled ?
          <Grid
            size={{
              xs: 12
            }}>
            <DisplayStringWithReplacements
              display_string={my_value}
              cfs={cfs}
            />
          </Grid>
        :
          <>
            <Grid
              size={{
                xs: 12
              }}>
              { view_plain ? 
                <DisplayStringWithReplacements
                  display_string={my_value}
                  cfs={cfs}
                />
              : 
                <StringBox 
                  name={name}
                  value={my_value}
                  multiline
                  color={"white"}
                  change_time={change_time}
                  onBlur={(changed: string) => {
                    setValue(changed.trim());
                    onChange(changed);
                  }}
                />
              }
            </Grid>
            <Grid
              size={{
                xs: 12
              }}><ReusableStringBox /></Grid>
            <Grid
              size={{
                xs: 2
              }}>
              <ButtonBox
                name="Add Tags to Bottom"
                onClick={() => {
                  let changed = my_value;
                  if (changed.length > 0) {
                    changed += "\n";
                  }
                  changed += "<table><tr><th></th><th></th></tr>\n<tr><td></td><td></td></tr>\n</table>\n<b></b>";
                  setValue(changed.trim());
                  onChange(changed);
                  // setReloading(true);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 2
              }}>
              <ButtonBox
                name="Auto Tag"
                disabled={disabled}
                onClick={() => {
                  let changed = "";
                  // // Search for certain things in the string to wrap with tags.
                  // changed = changed.replace("Casting Time:", "<b>Casting Time:</b>");
                  // changed = changed.replace("Range:", "<b>Range:</b>");
                  // changed = changed.replace("Components:", "<b>Components:</b>");
                  // changed = changed.replace("Duration:", "<b>Duration:</b>");
                  // changed = changed.replace("Cantrip Upgrade.", "<b>Cantrip Upgrade.</b>");
                  // changed = changed.replace("Using a Higher-Level Spell Slot.", "<b>Using a Higher-Level Spell Slot.</b>");
                  // changed = changed.replace("Cantrip Upgrade:", "<b>Cantrip Upgrade:</b>");
                  // changed = changed.replace("Using a Higher-Level Spell Slot:", "<b>Using a Higher-Level Spell Slot:</b>");
                  // changed = changed.replace("Ability Score Increase.", "<b>Ability Score Increase.</b>");

                  const lines = my_value.split("\n");
                  for (const line of lines) {
                    let line2 = line;
                    // Also do it if it starts with a certain string
                    if (line2.startsWith("Using a Higher-Level Spell Slot.")) {
                      line2 = line2.replace("Using a Higher-Level Spell Slot.", "<b>Using a Higher-Level Spell Slot.</b>");
                    } else if (line2.startsWith("Using a Higher-Level Spell Slot:")) {
                      line2 = line2.replace("Using a Higher-Level Spell Slot:", "<b>Using a Higher-Level Spell Slot:</b>");
                    } else {
                      // Look for the first '.' or ':'
                      let index = line2.indexOf(".");
                      const index2 = line2.indexOf(":");
                      if (index2 > 0) {
                        if (index === -1 || index > index2) {
                          index = index2;
                        }
                      }
                      if (index > 0) {
                        // Check the number of words before it.
                        const sub = line2.substring(0, index + 1);
                        const words = sub.split(" ");
                        if (words.length < 5 && !words.some(o => o.includes("("))) {
                          // If it's < 5 then bold everything until after it
                          line2 = line2.replace(sub, `<b>${sub}</b>`);
                        }
                      }
                    }
                    changed += line2 + "\n";
                  }
                  changed = common_util.replaceAll(changed, "<b><b>", "<b>");
                  changed = common_util.replaceAll(changed, "</b></b>", "</b>");

                  setValue(changed.trim());
                  onChange(changed);
                  // setReloading(true);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 2
              }}>
              <ButtonBox
                name="Replacers"
                onClick={() => {
                  let changed = my_value;
                  if (changed.length > 0) {
                    changed += "\n";
                  }
                  changed += "{attack_string.stat.zN}\n{dc_string.stat.zN}\n{potence_string.x.y.zN}\n{avg(countdsize+flatTotal)}\n{sum(zN)}";
                  setValue(changed.trim());
                  onChange(changed);
                  // setReloading(true);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 2
              }}>
              <ToggleButtonBox
                name="View Plain"
                value={view_plain}
                onToggle={() => {
                  setViewPlain(!view_plain);
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 2
              }}>
              <ButtonBox
                name="Remove Line Breaks"
                onClick={() => {
                  const changed = common_util.removeLineBreaks(my_value);
                  setValue(changed);
                  onChange(changed);
                  // setReloading(true);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 2
              }}>
              <ButtonBox
                name="Remove Attrs"
                onClick={() => {
                  const changed = removeTagAttributes(my_value);
                  setValue(changed);
                  onChange(changed);
                  // setReloading(true);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
          </>
        }
      </Grid>
    </div>
  ); 
}
